import PropTypes from 'prop-types';

import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

import { DashboardLayout } from '../../layouts';
import Box, { BoxProps as MuiBoxProps } from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { DataList, DataListComp } from '../../components/DataList';
import { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@classity-edtech/ui-base';
import { LanguageCode, LessonStatus, Roles } from '@classity-edtech/platform-utils';
import { useGetMyLessonsQuery } from '../../services/lessons';
import { Calendar } from '@classity-edtech/ui-base/dist/components/Displays';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { useNavigate } from 'react-router-dom';
import { webPaths } from '../../routes/paths';
import Helmet from '../../components/Helmet';
import { useGetMyProfileQuery } from '../../services/users';

export interface HomeProps extends MuiBoxProps {}

export const Tuitions = ({ className }: HomeProps) => {
  const defaultLanguage = LanguageCode.EN;
  const limit = 5;

  const { data: user } = useGetMyProfileQuery();

  const [loadingClient, setLoadingClient] = useState(true);
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const tabs = [
    { label: 'All', value: undefined },
    { label: 'Upcoming', value: LessonStatus.SCHEDULED },
    { label: 'Completed', value: LessonStatus.COMPLETED },
    { label: 'Cancelled', value: LessonStatus.CANCELLED },
  ];
  const [currentTab, setCurrentTab] = useState(tabs[0]?.value);

  const earliest = useMemo(() => dayjs().subtract(1, 'year').toISOString(), []);
  const latest = useMemo(() => dayjs().add(1, 'year').toISOString(), []);

  const {
    data: lessons,
    isLoading: loadingLessons,
    isFetching: fetchingLessons,
    isError: isLessonsError,
  } = useGetMyLessonsQuery(
    {
      earliest,
      latest,
      page,
      limit,
      status: currentTab,
      offset: (page - 1) * limit,
    },
    { skip: !user?.id },
  );

  const handlePageClick = (page: number) => {
    setPage(page);
  };

  const refinedLessonsData = isLessonsError ? [] : lessons?.data;
  const refinedLessonsPagination = isLessonsError ? undefined : lessons?.pagination;

  const dataList = {
    header: 'Private Tuition',
    type: 'dataList',
    tabs: tabs?.map(tab => ({
      label: tab.label,
      active: currentTab === tab?.value,
      action: () => setCurrentTab(tab?.value),
    })),
    column1Header: 'Status',
    column2Header: 'Updated',
    transactionButtons: refinedLessonsData?.map(l => {
      const name = l?.name?.find(n => n?.language === defaultLanguage)?.displayName;

      return {
        column1: {
          description: name,
        },
        column2: {
          description: `${LessonStatus.SCHEDULED.charAt(0).toUpperCase()}${LessonStatus.SCHEDULED.slice(1).toLowerCase()}`,
        },
        column3: { description: dayjs(l?.meta?.updated || l?.meta?.created).fromNow() },
        action: () => {},
        icon: (
          <Box
            sx={{
              width: '48px',
              height: '48px',
              borderRadius: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              background: globalColours['$global-colour-background-neutral-default'],
            }}
          >
            <Calendar
              sx={{
                width: '16px',
                height: '16px',
                color: globalColours['$global-colour-content-tertiary'],
              }}
            />
          </Box>
        ),
      };
    }),
    ...(user?.roleId === Roles.STUDENT && {
      newTransactionButton: {
        label: 'Book a new tuition session',
        action: () => navigate(`/${webPaths.studentBookTuition}`),
      },
    }),
    currentPage: page,
    numPages: refinedLessonsPagination?.pages,
    handlePageClick,
    isFetching: fetchingLessons,
  } as DataList;

  const appointmentAlert = {
    profile: {
      name: 'Wale Soyinka',
      src: 'https://avatars.githubusercontent.com/u/66563324?s=400&u=5b2223eb130d132087cc3aa630c3e088d8fa7f12&v=4',
      rating: 4.9,
      followers: 14,
    },
    cta: {
      label: 'Join',
      action: () => {},
    },
    appointmentDate: new Date('2025-05-02T20:01:20.713Z'),
    info: 'Start interview with...',
  };

  useEffect(() => {
    setLoadingClient(false);
  }, []);

  return (
    <>
      <Helmet />
      <DashboardLayout>
        {loadingLessons || loadingClient ? (
          <Box
            sx={{
              width: '100%',
              height: '80vh',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <DataListComp dataList={dataList} appointmentAlert={appointmentAlert} />
        )}
      </DashboardLayout>
    </>
  );
};

Tuitions.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Tuitions.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Tuitions)(
  ({ theme }) => `
`,
);
